import { t } from '@lingui/macro';
import { useOpenFolder } from 'components/FolderViewer';
import {
  CalendarIcon,
  FolderOpenIcon,
  GoogleDriveIcon,
  InfoIcon,
  UserIcon,
  VaultIcon,
} from 'components/icons';
import { FileIcon } from 'components/icons/FileIcon';
import React from 'react';
import { generatePath } from 'react-router-dom';
import { graphql, useFragment } from 'utils/graphClient';
import {
  ATTACHMENTS_PATH_PREFIX,
  EVENT_PATH,
  MEMBER_PATH,
  SPACE_PATH,
  VAULTS_MANAGER_VAULT_PATH,
} from 'utils/routes';
import { MenuLink } from './index';
import { FavouriteLink_node$key } from './__generated__/FavouriteLink_node.graphql';
import { BridgeIcon } from 'components/icons/BridgeIcon';
import { isGoogleDriveUrl } from 'utils/isGoogleDriveUrl';
import { isInternalUrl } from 'utils/isInternalUrl';

const favouriteLink_node = graphql`
  fragment FavouriteLink_node on Node {
    __typename
    id
    ... on Folder {
      name
      space {
        id
      }
    }
    ... on FolderItem {
      name
      content {
        __typename
        ... on File {
          mimeType
        }
        ... on BridgeInterface {
          url
        }
      }
    }
    ... on VaultItem {
      name
      content {
        __typename
        ... on File {
          name
          mimeType
        }
      }
    }
    ... on Event {
      name
    }
    ... on Member {
      name
    }
    ... on Vault {
      member {
        id
        name
      }
    }
    ... on AnnouncementAttachment {
      content {
        ... on File {
          name
          mimeType
        }
      }
    }
    ... on MemberDetailsAttachment {
      content {
        ... on File {
          name
          mimeType
        }
      }
    }
  }
`;

interface FavouriteLinkProps {
  node: FavouriteLink_node$key;
  isMobile?: boolean;
}

export const FavouriteLink = ({
  node: _node,
  isMobile,
}: FavouriteLinkProps) => {
  const node = useFragment(favouriteLink_node, _node);
  const { openFolderTo } = useOpenFolder();

  switch (node.__typename) {
    case 'Folder':
      if (!node.space?.id) {
        return <MissingFavouriteLink />;
      }
      return (
        <MenuLink
          label={node.name || t`Folder name unavailable.`}
          to={
            generatePath(SPACE_PATH, { spaceId: node.space.id }) +
            `/${ATTACHMENTS_PATH_PREFIX}` +
            openFolderTo(node.id)
          }
          icon={<FolderOpenIcon />}
          isMobile={isMobile}
        />
      );
    case 'FolderItem':
    case 'VaultItem':
      if (node.content?.__typename.includes('Bridge')) {
        if (node.content.url && isGoogleDriveUrl(node.content.url)) {
          return (
            <MenuLink
              label={node.name || t`Item name unavailable.`}
              toItemViewer={node.id}
              icon={<GoogleDriveIcon />}
              isMobile={isMobile}
            />
          );
        }

        if (isInternalUrl(node.content.url || '')) {
          const { pathname, search, hash } = new URL(node.content.url ?? '');
          return (
            <MenuLink
              label={node.name || t`Item name unavailable.`}
              to={{
                pathname: pathname,
                search: search,
                hash: hash,
              }}
              icon={<BridgeIcon />}
              isMobile={isMobile}
            />
          );
        } else {
          return (
            <MenuLink
              label={node.name || t`Item name unavailable.`}
              toExternal={node.content.url || ''}
              icon={<BridgeIcon />}
              isMobile={isMobile}
            />
          );
        }
      }
      return (
        <MenuLink
          label={node.name || t`Item name unavailable.`}
          toItemViewer={node.id}
          icon={
            <FileIcon
              mimeType={
                node.content?.__typename === 'File' ? node.content.mimeType : ''
              }
            />
          }
          isMobile={isMobile}
        />
      );
    case 'Event':
      return (
        <MenuLink
          label={node.name || t`Event name unavailable.`}
          to={generatePath(EVENT_PATH, { eventId: node.id })}
          icon={<CalendarIcon />}
          isMobile={isMobile}
        />
      );
    case 'Member':
      return (
        <MenuLink
          label={node.name || t`Member name unavailable.`}
          to={generatePath(MEMBER_PATH, { memberId: node.id })}
          icon={<UserIcon />}
          isMobile={isMobile}
        />
      );
    case 'Vault':
      return (
        <MenuLink
          label={node.member?.name || t`Vault name unavailable.`}
          to={generatePath(VAULTS_MANAGER_VAULT_PATH, { vaultId: node.id })}
          icon={<VaultIcon />}
          isMobile={isMobile}
        />
      );
    case 'AnnouncementAttachment':
      return (
        <MenuLink
          label={
            (node.content?.__typename === 'File' && node.content.name) ||
            t`Name unavailable.`
          }
          toItemViewer={node.id}
          icon={<VaultIcon />}
          isMobile={isMobile}
        />
      );
    case 'MemberDetailsAttachment':
      return (
        <MenuLink
          label={
            (node.content?.__typename === 'File' && node.content.name) ||
            t`Name unavailable.`
          }
          toItemViewer={node.id}
          icon={<VaultIcon />}
          isMobile={isMobile}
        />
      );
    default:
      return <MissingFavouriteLink />;
  }
};

const MissingFavouriteLink = ({ name }: { name?: string }) => {
  return (
    <MenuLink
      label={name || t`Item unavailable`}
      to=""
      isDisabled
      icon={<InfoIcon />}
    />
  );
};
